import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Upload, FileText, CheckCircle2, XCircle, Repeat } from "lucide-react";

export default function PreRegistrationNew() {
  const nav = useNavigate();
  const [tab, setTab] = useState("single"); // single | csv | series
  return (
    <AppShell>
      <button onClick={() => nav("/pre-registrations")} className="btn-ghost text-xs mb-3" data-testid="new-pr-back">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to pre-registrations
      </button>
      <h1 className="text-3xl font-semibold mb-2">New pre-registration</h1>
      <p className="text-sm text-navy-500 mb-6">Create a single visit, upload a CSV, or set up a recurring series.</p>

      <div className="flex gap-1 mb-6 border-b border-navy-100" data-testid="new-pr-tabs">
        {[
          { k: "single", label: "Single visit", icon: FileText },
          { k: "csv", label: "Bulk CSV", icon: Upload },
          { k: "series", label: "Recurring series", icon: Repeat },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 inline-flex items-center gap-2 ${tab === t.k ? "border-orange-500 text-orange-600" : "border-transparent text-navy-600"}`}
            data-testid={`new-pr-tab-${t.k}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "single" && <SingleForm onDone={() => nav("/pre-registrations")} />}
      {tab === "csv" && <CsvUpload />}
      {tab === "series" && <SeriesForm onDone={() => nav("/pre-registrations")} />}
    </AppShell>
  );
}

function useSitesAndHosts() {
  const [sites, setSites] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [types, setTypes] = useState([]);
  const [me, setMe] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const [s, u, t, m] = await Promise.all([
          api.get("/sites"),
          api.get("/users"),
          api.get("/visitor-types"),
          api.get("/auth/me"),
        ]);
        setSites(s.data || []);
        setHosts((u.data || []).filter((x) => x.role === "host"));
        setTypes((t.data || []).filter((x) => x.active !== false));
        setMe(m.data?.user);
      } catch (e) {
        // host role can't list users → fall back: only allow self
        try {
          const m = await api.get("/auth/me");
          setMe(m.data?.user);
          if (m.data?.user) {
            setHosts([{ id: m.data.user.id, email: m.data.user.email, name: m.data.user.name, role: "host" }]);
          }
        } catch (e) { console.warn("[PreRegistrationNew] /auth/me fallback failed", e); }
      }
    })();
  }, []);
  return { sites, hosts, types, me };
}

function SingleForm({ onDone }) {
  const { sites, hosts, types, me } = useSitesAndHosts();
  const [form, setForm] = useState({
    visitor_name: "", visitor_email: "", visitor_phone: "",
    company: "", host_id: "", site_id: "", expected_at: "",
    visitor_type: "Visitor", purpose: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setForm((f) => ({
      ...f,
      site_id: f.site_id || sites[0]?.id || "",
      host_id: f.host_id || (me?.role === "host" ? me.id : (hosts[0]?.id || "")),
    }));
  }, [sites, hosts, me]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const expectedISO = new Date(form.expected_at).toISOString();
      const { data } = await api.post("/pre-registrations", { ...form, expected_at: expectedISO });
      setPreview(data);
      toast.success("Pre-registration created — invitation queued (mocked).");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className="eb-card p-6 space-y-4" data-testid="single-form">
        <Field label="Visitor name" required><input className="eb-input" required value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} data-testid="pr-visitor-name" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Visitor email"><input type="email" className="eb-input" value={form.visitor_email} onChange={(e) => setForm({ ...form, visitor_email: e.target.value })} data-testid="pr-visitor-email" /></Field>
          <Field label="Visitor phone"><input className="eb-input" value={form.visitor_phone} onChange={(e) => setForm({ ...form, visitor_phone: e.target.value })} data-testid="pr-visitor-phone" /></Field>
        </div>
        <Field label="Company"><input className="eb-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="pr-company" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Site" required>
            <select required className="eb-input" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} data-testid="pr-site">
              <option value="">—</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Host" required>
            <select required className="eb-input" value={form.host_id} onChange={(e) => setForm({ ...form, host_id: e.target.value })} data-testid="pr-host">
              <option value="">—</option>
              {hosts.map((h) => <option key={h.id} value={h.id}>{h.name || h.email}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Expected date & time" required>
            <input type="datetime-local" required className="eb-input" value={form.expected_at} onChange={(e) => setForm({ ...form, expected_at: e.target.value })} data-testid="pr-expected-at" />
          </Field>
          <Field label="Visitor type" required>
            <select required className="eb-input" value={form.visitor_type} onChange={(e) => setForm({ ...form, visitor_type: e.target.value })} data-testid="pr-visitor-type">
              {types.length === 0 ? <option value="Visitor">Visitor</option> : types.map((t) => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Purpose"><input className="eb-input" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="pr-purpose" /></Field>
        <Field label="Notes (private)"><textarea rows={3} className="eb-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pr-notes" /></Field>
        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="btn-accent" data-testid="pr-create-btn">
            {saving ? "Creating…" : "Create pre-registration"}
          </button>
        </div>
      </form>
      <div className="eb-card p-6" data-testid="pr-preview">
        <div className="eb-overline mb-3">Invitation preview</div>
        {!preview ? (
          <p className="text-sm text-navy-500">Fill the form and click Create — the invitation preview will appear here.</p>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="rounded-lg border border-orange-200 bg-orange-50/40 p-4">
              <div className="font-semibold text-navy-900">Invitation queued for {preview.visitor_email || preview.visitor_phone}</div>
              <div className="text-navy-500 text-xs mt-1">Mode: {preview.invitation_send?.mode || "mock"}</div>
            </div>
            <Row label="QR token (truncated)"><code className="text-xs">{(preview.qr_token || "").slice(0, 64)}…</code></Row>
            <Row label="Status">{preview.invitation_status}</Row>
            <button onClick={onDone} className="btn-outline" data-testid="pr-done-btn">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function CsvUpload() {
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/pre-registrations/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "pre_reg_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pre-registrations/csv-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.accepted}, rejected ${data.rejected.length}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="eb-card p-6" data-testid="csv-card">
        <div className="eb-overline mb-2">Bulk import</div>
        <h2 className="text-lg font-semibold mb-2">Upload a CSV</h2>
        <p className="text-sm text-navy-500 mb-4">Up to 5 MB and 1,000 rows. Required columns: visitor_name, host_email, expected_at_iso, visitor_type. Either visitor_email or visitor_phone is required.</p>
        <button onClick={downloadTemplate} className="btn-outline mb-4" data-testid="csv-template-btn">
          <FileText className="w-4 h-4" /> Download template
        </button>
        <label className="block">
          <div className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${uploading ? "border-orange-300 bg-orange-50/40" : "border-navy-200 hover:border-orange-300"}`}>
            <Upload className="w-8 h-8 text-navy-400 mx-auto mb-2" />
            <div className="text-sm font-medium">{uploading ? "Uploading…" : "Click to choose a CSV file"}</div>
            <div className="text-xs text-navy-500 mt-1">Or drag & drop</div>
          </div>
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={onUpload} data-testid="csv-file-input" />
        </label>
      </div>
      <div className="eb-card p-6" data-testid="csv-result">
        <div className="eb-overline mb-2">Result</div>
        {!result ? (
          <p className="text-sm text-navy-500">Upload a CSV to see import results here.</p>
        ) : (
          <>
            <div className="flex gap-3 mb-4">
              <div className="flex-1 rounded-lg border border-success/40 bg-success/5 p-3">
                <div className="text-xs uppercase text-success font-bold">Accepted</div>
                <div className="text-2xl font-bold text-success">{result.accepted}</div>
              </div>
              <div className="flex-1 rounded-lg border border-danger/40 bg-danger/5 p-3">
                <div className="text-xs uppercase text-danger font-bold">Rejected</div>
                <div className="text-2xl font-bold text-danger">{result.rejected.length}</div>
              </div>
            </div>
            {result.rejected.length > 0 && (
              <div className="border border-navy-100 rounded-lg overflow-hidden">
                <div className="bg-navy-50 px-3 py-2 text-xs font-semibold text-navy-700">Rejected rows</div>
                <ul className="divide-y divide-navy-100 text-sm max-h-[280px] overflow-y-auto">
                  {result.rejected.map((r, i) => (
                    <li key={i} className="px-3 py-2 flex items-start gap-2">
                      <XCircle className="w-3.5 h-3.5 text-danger mt-0.5 flex-shrink-0" />
                      <div><strong>Row {r.row_number}:</strong> <span className="text-navy-600">{r.reason}</span></div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="text-xs text-navy-500 mt-3">Batch ID: <code>{result.batch_id?.slice(0, 8)}…</code></div>
          </>
        )}
      </div>
    </div>
  );
}

function SeriesForm({ onDone }) {
  const { sites, hosts, types, me } = useSitesAndHosts();
  const [form, setForm] = useState({
    visitor_name: "", visitor_email: "", visitor_type: "Visitor", purpose: "",
    company: "", host_id: "", site_id: "",
    first_expected_at: "",
    freq: "WEEKLY", interval: 1, by_day: ["MO"], count: 8, until: "",
  });
  const [saving, setSaving] = useState(false);
  const DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
  const toggleDay = (d) => setForm((f) => ({ ...f, by_day: f.by_day.includes(d) ? f.by_day.filter((x) => x !== d) : [...f.by_day, d] }));

  useEffect(() => {
    setForm((f) => ({
      ...f,
      site_id: f.site_id || sites[0]?.id || "",
      host_id: f.host_id || (me?.role === "host" ? me.id : (hosts[0]?.id || "")),
    }));
  }, [sites, hosts, me]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const rule = {
        freq: form.freq, interval: Number(form.interval) || 1,
        by_day: form.freq === "WEEKLY" ? form.by_day : null,
        count: form.until ? null : Number(form.count) || null,
        until: form.until || null,
      };
      const tpl = {
        name: form.visitor_name, email: form.visitor_email,
        type: form.visitor_type, purpose: form.purpose, company: form.company,
      };
      const { data } = await api.post("/pre-registration-series", {
        visitor_template: tpl,
        host_id: form.host_id,
        site_id: form.site_id,
        first_expected_at: new Date(form.first_expected_at).toISOString(),
        recurrence_rule: rule,
      });
      toast.success("Series created — first 7 days will expand on the next nightly cron, or trigger from super-admin.");
      onDone(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="eb-card p-6 space-y-4 max-w-3xl" data-testid="series-form">
      <h2 className="text-lg font-semibold">Recurring series</h2>
      <Field label="Visitor name (template)" required><input required className="eb-input" value={form.visitor_name} onChange={(e) => setForm({ ...form, visitor_name: e.target.value })} data-testid="series-name" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Visitor email"><input type="email" className="eb-input" value={form.visitor_email} onChange={(e) => setForm({ ...form, visitor_email: e.target.value })} data-testid="series-email" /></Field>
        <Field label="Company"><input className="eb-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Site" required>
          <select required className="eb-input" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Host" required>
          <select required className="eb-input" value={form.host_id} onChange={(e) => setForm({ ...form, host_id: e.target.value })}>
            {hosts.map((h) => <option key={h.id} value={h.id}>{h.name || h.email}</option>)}
          </select>
        </Field>
      </div>
      <Field label="First occurrence" required>
        <input type="datetime-local" required className="eb-input" value={form.first_expected_at} onChange={(e) => setForm({ ...form, first_expected_at: e.target.value })} data-testid="series-first" />
      </Field>
      <div className="rounded-lg border border-navy-100 p-4 space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Frequency">
            <select className="eb-input" value={form.freq} onChange={(e) => setForm({ ...form, freq: e.target.value })} data-testid="series-freq">
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
              <option value="MONTHLY">Monthly</option>
            </select>
          </Field>
          <Field label="Interval"><input type="number" min={1} max={12} className="eb-input" value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })} /></Field>
          <Field label="Count (max 52)"><input type="number" min={1} max={52} className="eb-input" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} data-testid="series-count" /></Field>
        </div>
        {form.freq === "WEEKLY" && (
          <div>
            <div className="eb-label">Days of week</div>
            <div className="flex gap-2">
              {DAYS.map((d) => (
                <button type="button" key={d} onClick={() => toggleDay(d)}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold border ${form.by_day.includes(d) ? "bg-orange-500 text-white border-orange-500" : "bg-white text-navy-700 border-navy-200"}`}
                  data-testid={`series-day-${d}`}
                >{d}</button>
              ))}
            </div>
          </div>
        )}
        <Field label="Until (optional, overrides count)">
          <input type="date" className="eb-input" value={form.until} onChange={(e) => setForm({ ...form, until: e.target.value })} data-testid="series-until" />
        </Field>
      </div>
      <div className="text-xs text-navy-500">Caps: max 52 occurrences per series · max 20 active series per host.</div>
      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="btn-accent" data-testid="series-create-btn">
          {saving ? "Creating…" : "Create series"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="eb-label">{label}{required && <span className="text-orange-500"> *</span>}</label>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="text-navy-400 uppercase tracking-wider text-[10px] font-semibold">{label}</div>
      <div className="col-span-2 text-sm">{children}</div>
    </div>
  );
}
